/**
 * 图片缓存管理工具
 * 提供缓存统计、清理、导出等功能
 */
import imageCache from './imageCache'

class ImageCacheManager {
  /**
   * 获取详细的缓存统计信息
   */
  getDetailedStats() {
    const stats = imageCache.getCacheStats()
    const storageKeys = Object.keys(localStorage).filter(key => 
      key.startsWith(imageCache.storagePrefix)
    )
    
    const storageDetails = storageKeys.map(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key))
        return {
          key: key.replace(imageCache.storagePrefix, ''),
          url: this.reverseHashUrl(key.replace(imageCache.storagePrefix, '')),
          size: Math.round(data.base64.length / 1024 * 100) / 100, // KB
          timestamp: new Date(data.timestamp).toLocaleString(),
          age: Math.round((Date.now() - data.timestamp) / 1000 / 60 / 60 * 100) / 100 // 小时
        }
      } catch {
        return null
      }
    }).filter(Boolean)

    return {
      ...stats,
      storageDetails,
      totalItems: stats.memory.count + stats.storage.count,
      oldestCache: storageDetails.length > 0 ? 
        Math.max(...storageDetails.map(item => Date.now() - new Date(item.timestamp).getTime())) / 1000 / 60 / 60 : 0
    }
  }

  /**
   * 清理特定时间之前的缓存
   * @param {number} hours 小时数
   */
  cleanCacheOlderThan(hours = 24) {
    const cutoffTime = Date.now() - (hours * 60 * 60 * 1000)
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(imageCache.storagePrefix)
    )

    let cleanedCount = 0
    keys.forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key))
        if (data.timestamp < cutoffTime) {
          localStorage.removeItem(key)
          cleanedCount++
        }
      } catch {
        localStorage.removeItem(key)
        cleanedCount++
      }
    })

    console.log(`已清理 ${cleanedCount} 个超过 ${hours} 小时的缓存项`)
    return cleanedCount
  }

  /**
   * 清理大于指定大小的缓存项
   * @param {number} maxSizeKB 最大大小（KB）
   */
  cleanLargeCache(maxSizeKB = 500) {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(imageCache.storagePrefix)
    )

    let cleanedCount = 0
    keys.forEach(key => {
      try {
        const data = localStorage.getItem(key)
        const sizeKB = data.length / 1024
        if (sizeKB > maxSizeKB) {
          localStorage.removeItem(key)
          cleanedCount++
        }
      } catch {
        localStorage.removeItem(key)
        cleanedCount++
      }
    })

    console.log(`已清理 ${cleanedCount} 个大于 ${maxSizeKB}KB 的缓存项`)
    return cleanedCount
  }

  /**
   * 导出缓存配置（不包含实际图片数据）
   */
  exportConfig() {
    const stats = this.getDetailedStats()
    const config = {
      exportTime: new Date().toISOString(),
      cacheStats: {
        totalItems: stats.totalItems,
        memoryCount: stats.memory.count,
        storageCount: stats.storage.count,
        storageSize: stats.storage.size
      },
      settings: {
        maxCacheSize: imageCache.maxCacheSize,
        expireTime: imageCache.expireTime / (24 * 60 * 60 * 1000) // 转换为天数
      },
      urls: stats.storageDetails.map(item => ({
        url: item.url,
        size: item.size,
        age: item.age
      }))
    }

    const blob = new Blob([JSON.stringify(config, null, 2)], { 
      type: 'application/json' 
    })
    const url = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = `image-cache-config-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    
    URL.revokeObjectURL(url)
    console.log('缓存配置已导出')
  }

  /**
   * 预热缓存 - 预加载当前页面可能用到的图片
   */
  async warmupCache() {
    const userInfo = window.Vue?.prototype?.$store?.state?.userStore?.userInfo
    const friends = window.Vue?.prototype?.$store?.state?.friendStore?.friends || []
    
    const urls = []
    
    // 添加当前用户头像
    if (userInfo?.headImage) {
      urls.push(userInfo.headImage)
    }
    if (userInfo?.headImageThumb) {
      urls.push(userInfo.headImageThumb)
    }
    
    // 添加好友头像（前10个）
    friends.slice(0, 10).forEach(friend => {
      if (friend.headImage) urls.push(friend.headImage)
      if (friend.headImageThumb) urls.push(friend.headImageThumb)
    })
    
    if (urls.length > 0) {
      console.log(`开始预热缓存，预加载 ${urls.length} 个图片`)
      const results = await imageCache.preloadImages(urls)
      const successCount = results.filter(r => r.status === 'fulfilled').length
      console.log(`缓存预热完成：${successCount}/${urls.length} 个图片加载成功`)
    }
  }

  /**
   * 反向哈希（仅用于调试，实际无法完全反向）
   */
  reverseHashUrl(hash) {
    // 由于哈希是不可逆的，这里只返回哈希值作为标识
    return `[hash:${hash}]`
  }

  /**
   * 安装调试工具到 window 对象
   */
  installDebugTools() {
    if (typeof window !== 'undefined') {
      window.imageCacheManager = {
        stats: () => this.getDetailedStats(),
        clean: (hours) => this.cleanCacheOlderThan(hours),
        cleanLarge: (sizeKB) => this.cleanLargeCache(sizeKB),
        clear: () => imageCache.clearCache(),
        export: () => this.exportConfig(),
        warmup: () => this.warmupCache(),
        help: () => {
          console.log(`
图片缓存管理工具使用说明：

1. imageCacheManager.stats() - 查看缓存统计
2. imageCacheManager.clean(24) - 清理24小时前的缓存
3. imageCacheManager.cleanLarge(500) - 清理大于500KB的缓存
4. imageCacheManager.clear() - 清空所有缓存
5. imageCacheManager.export() - 导出缓存配置
6. imageCacheManager.warmup() - 预热缓存
7. imageCacheManager.help() - 显示帮助信息

示例：
- 查看缓存状态：imageCacheManager.stats()
- 清理旧缓存：imageCacheManager.clean(48) // 清理48小时前的
- 清理大文件：imageCacheManager.cleanLarge(200) // 清理大于200KB的
          `)
        }
      }
      
      console.log('图片缓存管理工具已安装到 window.imageCacheManager')
      console.log('输入 imageCacheManager.help() 查看使用说明')
    }
  }
}

// 创建管理器实例并安装调试工具
const manager = new ImageCacheManager()

// 在开发环境中自动安装调试工具
if (process.env.NODE_ENV === 'development') {
  manager.installDebugTools()
}

export default manager 